/**
 * Render-on-demand frame loop, modeled on the Unreal viewport's "Realtime" toggle.
 *
 * A frame runs only when something asks for one:
 * - `invalidate()`: state changed, render one more frame.
 * - `beginAnimation()`: a transient animation (camera fly, transition) keeps
 *   frames coming until released, regardless of idleness.
 * - `setAmbientAnimation()`: continuous decoration (VFX, character idles) keeps
 *   frames coming. With `setPauseWhenIdle(true)`, only until the viewport has
 *   been idle for `idleTimeoutMs`: then the loop stops, the last frame stays on
 *   screen (SPEC §10), and input resumes it.
 *
 * Each frame runs its phases in a fixed order so systems see a consistent
 * pipeline: input → systems → transforms → bridge → render.
 *
 * Frames carry two clocks. `time`/`dt` are wall time, for the editor (camera
 * flights, frame timings). `worldTime`/`worldDt` are the level's time, scaled
 * by `setWorldTimeScale()` (Unreal's global time dilation): what animates in
 * the world reads it, so a scale of 0 freezes the world while the viewport
 * still redraws.
 */
import type { Clock } from "./Clock";
import { FrameTimings } from "./frameTimings";
import type { FrameTimingSnapshot } from "./frameTimings";
import type { Unsubscribe } from "./Emitter";

export type FramePhase = "input" | "systems" | "transforms" | "bridge" | "render";

const PHASE_ORDER: readonly FramePhase[] = ["input", "systems", "transforms", "bridge", "render"];

export interface FrameInfo {
  /** Seconds since engine start. */
  readonly time: number;
  /** Seconds since the previous frame (0 for the first frame after idling). */
  readonly dt: number;
  /** Monotonic frame counter. */
  readonly frame: number;
  /** Seconds of world time: advances by `worldDt` each frame, and stands still at scale 0. */
  readonly worldTime: number;
  /** World seconds since the previous frame: `dt` times the world time scale. */
  readonly worldDt: number;
}

/** Abstraction over `requestAnimationFrame`, so tests can step frames manually. */
export interface FrameScheduler {
  request(callback: () => void): void;
  cancel(): void;
}

export interface FrameLoopOptions {
  clock: Clock;
  frames: FrameScheduler;
  /** Ambient animation stops after this long without input. */
  idleTimeoutMs: number;
}

export class FrameLoop {
  private readonly clock: Clock;
  private readonly frames: FrameScheduler;
  private readonly idleTimeoutMs: number;
  private readonly callbacks = new Map<FramePhase, Set<(frame: FrameInfo) => void>>();

  private readonly transient = new Set<symbol>();
  private readonly ambient = new Set<string>();
  private invalidated = false;
  private paused = false;
  private pauseWhenIdle = true;
  private scheduled = false;

  private lastInputMs: number;
  private lastFrameMs: number | null = null;
  private frameCount = 0;
  private worldTime = 0;
  private worldTimeScale = 1;
  private readonly frameTimings = new FrameTimings();

  constructor(options: FrameLoopOptions) {
    this.clock = options.clock;
    this.frames = options.frames;
    this.idleTimeoutMs = options.idleTimeoutMs;
    this.lastInputMs = this.clock.now();
  }

  /** Registers a per-frame callback for a phase. */
  register(phase: FramePhase, callback: (frame: FrameInfo) => void): Unsubscribe {
    let set = this.callbacks.get(phase);
    if (!set) {
      set = new Set();
      this.callbacks.set(phase, set);
    }
    set.add(callback);
    return () => {
      set.delete(callback);
    };
  }

  /** Requests one more frame. `reason` documents intent at call sites. */
  invalidate(_reason: string): void {
    this.invalidated = true;
    this.ensureScheduled();
  }

  /** Keeps frames running until the returned release function is called. */
  beginAnimation(_source: string): () => void {
    const token = Symbol();
    this.transient.add(token);
    this.ensureScheduled();

    return () => {
      this.transient.delete(token);
    };
  }

  /** Turns an idle-gated continuous animation on or off. */
  setAmbientAnimation(source: string, active: boolean): void {
    if (active) this.ambient.add(source);
    else this.ambient.delete(source);
    this.ensureScheduled();
  }

  /** Whether ambient animation stops after `idleTimeoutMs` without input (a battery saver). */
  setPauseWhenIdle(pause: boolean): void {
    this.pauseWhenIdle = pause;
    this.ensureScheduled();
  }

  /**
   * How fast world time runs against wall time: 1 is normal, 0 freezes it
   * (Pause, Realtime off). It changes what frames report, not whether they run.
   */
  setWorldTimeScale(scale: number): void {
    this.worldTimeScale = scale;
    this.invalidate("world time scale");
  }

  /** Records user input: resets the idle timer and wakes the loop. */
  noteInput(): void {
    this.lastInputMs = this.clock.now();
    this.invalidate("input");
  }

  /** Paused while the tab is hidden or the viewport is covered. Resuming renders a fresh frame. */
  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;

    if (paused) {
      this.frames.cancel();
      this.scheduled = false;
      this.lastFrameMs = null;
    } else {
      this.invalidate("resume");
    }
  }

  get isRunning(): boolean {
    return this.scheduled;
  }

  /** Frames run since startup. */
  get framesRun(): number {
    return this.frameCount;
  }

  /** Recent frame rate, frame time and CPU time, for the viewport stats overlay. */
  timings(): FrameTimingSnapshot {
    return this.frameTimings.snapshot();
  }

  private wantsFrame(): boolean {
    if (this.invalidated || this.transient.size > 0) return true;
    return this.ambient.size > 0 && !(this.pauseWhenIdle && this.isIdle());
  }

  private isIdle(): boolean {
    return this.clock.now() - this.lastInputMs >= this.idleTimeoutMs;
  }

  /** Schedules a frame if one is wanted. Returns whether a frame is now pending. */
  private ensureScheduled(): boolean {
    if (this.scheduled) return true;
    if (this.paused || !this.wantsFrame()) return false;

    this.scheduled = true;
    this.frames.request(this.runFrame);
    return true;
  }

  private readonly runFrame = (): void => {
    this.scheduled = false;

    const nowMs = this.clock.now();
    const dt = this.lastFrameMs === null ? 0 : (nowMs - this.lastFrameMs) / 1000;
    const worldDt = dt * this.worldTimeScale;
    this.worldTime += worldDt;
    const frame: FrameInfo = {
      time: this.clock.sinceStart(),
      dt,
      frame: this.frameCount++,
      worldTime: this.worldTime,
      worldDt,
    };
    this.lastFrameMs = nowMs;

    // Cleared before running phases so a system can invalidate again for the next frame.
    this.invalidated = false;
    this.runPhases(frame);

    // The first frame after idling has no interval to measure.
    if (frame.dt > 0) this.frameTimings.record(frame.dt * 1000, this.clock.now() - nowMs);

    const continuing = this.ensureScheduled();
    if (!continuing) this.lastFrameMs = null; // the next frame after idling starts with dt = 0
  };

  private runPhases(frame: FrameInfo): void {
    for (const phase of PHASE_ORDER) {
      const set = this.callbacks.get(phase);
      if (!set) continue;
      for (const callback of [...set]) callback(frame);
    }
  }
}

/** The browser implementation of FrameScheduler. */
export class AnimationFrameScheduler implements FrameScheduler {
  private handle: number | null = null;

  request(callback: () => void): void {
    this.handle = requestAnimationFrame(() => {
      this.handle = null;
      callback();
    });
  }

  cancel(): void {
    if (this.handle !== null) cancelAnimationFrame(this.handle);
    this.handle = null;
  }
}
